import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { TransactionSummaryCards } from '@/components/transactions/TransactionSummaryCards';
import { TransactionDetailSheet } from '@/components/transactions/TransactionDetailSheet';
import { Filter, X, Download, Calendar, Euro, Building2, Landmark, Search } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

type ReconciliationStatus = 'all' | 'matched' | 'pending_review' | 'unreconciled' | 'excluded';

export default function Transactions() {
  const [filterSciId, setFilterSciId] = useState<string>('all');
  const [filterBankAccountId, setFilterBankAccountId] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<ReconciliationStatus>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch all transactions with related data
  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ['bank-transactions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bank_transactions')
        .select(`
          *,
          bank_accounts (
            id,
            account_name,
            iban,
            sci_id,
            scis (
              id,
              name
            )
          )
        `)
        .order('transaction_date', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Fetch SCIs for filter
  const { data: scis = [] } = useQuery({
    queryKey: ['scis-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('scis')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  // Fetch bank accounts for filter
  const { data: bankAccounts = [] } = useQuery({
    queryKey: ['bank-accounts-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bank_accounts')
        .select('id, account_name, iban, scis(name)')
        .eq('is_active', true)
        .order('account_name');
      if (error) throw error;
      return data;
    },
  });

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return transactions.filter((transaction: any) => {
      // Filter by SCI
      const sciId = transaction.bank_accounts?.scis?.id;
      if (filterSciId !== 'all' && sciId !== filterSciId) return false;

      // Filter by Bank Account
      const bankAccountId = transaction.bank_accounts?.id;
      if (filterBankAccountId !== 'all' && bankAccountId !== filterBankAccountId) return false;

      // Filter by Status
      if (filterStatus !== 'all' && transaction.reconciliation_status !== filterStatus) return false;

      // Filter by Date Range
      if (dateFrom && transaction.transaction_date < dateFrom) return false;
      if (dateTo && transaction.transaction_date > dateTo) return false;

      // Search filter
      if (term) {
        const description = (transaction.description || '').toLowerCase();
        const accountName = (transaction.bank_accounts?.account_name || '').toLowerCase();
        const sciName = (transaction.bank_accounts?.scis?.name || '').toLowerCase();
        const amount = String(transaction.amount || '');
        if (!description.includes(term) && !accountName.includes(term) && !sciName.includes(term) && !amount.includes(term)) {
          return false;
        }
      }

      return true;
    });
  }, [transactions, filterSciId, filterBankAccountId, filterStatus, dateFrom, dateTo, searchTerm]);

  const hasActiveFilters =
    filterSciId !== 'all' ||
    filterBankAccountId !== 'all' ||
    filterStatus !== 'all' ||
    dateFrom !== '' ||
    dateTo !== '' ||
    searchTerm !== '';

  // Calculate summary statistics
  const summary = useMemo(() => {
    const total = filteredTransactions.reduce((sum, t) => sum + (t.amount > 0 ? t.amount : 0), 0);
    const matched = filteredTransactions.filter(t => t.reconciliation_status === 'matched').length;
    const pendingReview = filteredTransactions.filter(t => t.reconciliation_status === 'pending_review').length;
    const unreconciled = filteredTransactions.filter(t => t.reconciliation_status === 'unreconciled').length;

    return { total, matched, pendingReview, unreconciled };
  }, [filteredTransactions]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'matched':
        return 'default';
      case 'pending_review':
        return 'secondary';
      case 'unreconciled':
        return 'outline';
      case 'excluded':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'matched':
        return 'Rapprochée';
      case 'pending_review':
        return 'En révision';
      case 'unreconciled':
        return 'Non rapprochée';
      case 'excluded':
        return 'Exclue';
      default:
        return status;
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'SCI', 'Compte', 'Montant', 'Description', 'Statut'];
    const rows = filteredTransactions.map((t: any) => [
      format(new Date(t.transaction_date), 'dd/MM/yyyy'),
      t.bank_accounts?.scis?.name || '',
      t.bank_accounts?.account_name || '',
      t.amount.toFixed(2),
      t.description || '',
      getStatusLabel(t.reconciliation_status),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `transactions_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  const columns = [
    {
      header: 'Date',
      accessorKey: (row: any) => (
        <div className="flex items-center gap-1 font-medium">
          <Calendar className="h-3 w-3 text-muted-foreground" />
          {format(new Date(row.transaction_date), 'dd/MM/yyyy', { locale: fr })}
        </div>
      ),
    },
    {
      header: 'SCI',
      accessorKey: (row: any) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-primary" />
          <span className="font-bold">{row.bank_accounts?.scis?.name || '-'}</span>
        </div>
      ),
    },
    {
      header: 'Compte bancaire',
      accessorKey: (row: any) => (
        <div className="flex items-center gap-2">
          <Landmark className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="font-medium">{row.bank_accounts?.account_name || '-'}</div>
            {row.bank_accounts?.iban && (
              <div className="text-xs text-muted-foreground">
                ...{row.bank_accounts.iban.slice(-4)}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      header: 'Montant',
      accessorKey: (row: any) => (
        <div className={`flex items-center gap-1 font-bold text-lg ${
          row.amount > 0 ? 'text-green-600' : 'text-red-600'
        }`}>
          <Euro className="h-4 w-4" />
          {row.amount > 0 ? '+' : ''}{row.amount.toFixed(2)} €
        </div>
      ),
    },
    {
      header: 'Description',
      accessorKey: (row: any) => (
        <div className="max-w-xs truncate" title={row.description}>
          {row.description || '-'}
        </div>
      ),
    },
    {
      header: 'Statut',
      accessorKey: (row: any) => (
        <Badge variant={getStatusColor(row.reconciliation_status) as any}>
          {getStatusLabel(row.reconciliation_status)}
        </Badge>
      ),
    },
    {
      header: 'Actions',
      accessorKey: (row: any) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSelectedTransaction(row)}
        >
          Détails
        </Button>
      ),
    },
  ];

  return (
    <AppLayout title="Transactions Bancaires" description="Visualisez et gérez toutes vos transactions">
      <PageHeader
        title="Transactions Bancaires"
        description={`${filteredTransactions.length} transaction${filteredTransactions.length > 1 ? 's' : ''}${hasActiveFilters ? ' (filtrées)' : ''}`}
        actions={
          <Button onClick={exportToCSV} variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Exporter CSV
          </Button>
        }
      />

      {/* Summary Cards */}
      <TransactionSummaryCards
        total={summary.total}
        matched={summary.matched}
        pendingReview={summary.pendingReview}
        unreconciled={summary.unreconciled}
      />

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6 p-4 bg-muted/50 rounded-lg flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />

        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher une transaction..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">SCI:</Label>
          <Select value={filterSciId} onValueChange={setFilterSciId}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Toutes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les SCI</SelectItem>
              {scis.map((sci: any) => (
                <SelectItem key={sci.id} value={sci.id}>{sci.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Compte:</Label>
          <Select value={filterBankAccountId} onValueChange={setFilterBankAccountId}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les comptes</SelectItem>
              {bankAccounts.map((account: any) => (
                <SelectItem key={account.id} value={account.id}>
                  {account.account_name} ({account.scis?.name})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Statut:</Label>
          <Select value={filterStatus} onValueChange={(value) => setFilterStatus(value as ReconciliationStatus)}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="matched">Rapprochées</SelectItem>
              <SelectItem value="pending_review">En révision</SelectItem>
              <SelectItem value="unreconciled">Non rapprochées</SelectItem>
              <SelectItem value="excluded">Exclues</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Du:</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-40"
          />
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Au:</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-40"
          />
        </div>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilterSciId('all');
              setFilterBankAccountId('all');
              setFilterStatus('all');
              setDateFrom('');
              setDateTo('');
              setSearchTerm('');
            }}
          >
            <X className="h-4 w-4 mr-1" /> Réinitialiser
          </Button>
        )}
      </div>

      {/* Transactions Table */}
      <DataTable
        columns={columns}
        data={filteredTransactions}
        isLoading={isLoading}
        emptyMessage="Aucune transaction trouvée. Connectez une banque et synchronisez les transactions."
      />

      {/* Transaction Detail Sheet */}
      <TransactionDetailSheet
        transaction={selectedTransaction}
        open={!!selectedTransaction}
        onOpenChange={(open) => !open && setSelectedTransaction(null)}
      />
    </AppLayout>
  );
}
